import {
   ChangeEvent,
   FC,
   ReactNode,
   useCallback,
   useEffect,
   useRef,
   useState,
} from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router"
import AuthLayout from "@/components/AuthLayout"
import LoginSupport from "@/components/LoginSupport"
import Button from "@/components/shared/Button"
import Link from "@/components/shared/Link"
import TextInput from "@/components/shared/TextInput"
import { PageRoutes } from "@/constants/routes"
import { AppDispatch, RootState } from "@/store"
import { loginVerify, resetAuthState } from "@/store/authSlice"
import { Variant } from "@/types/index"
import "./index.scss"

const CODE_LENGTH = 6
const INITIAL_SECONDS_LEFT = 600
const INVALID_CODE_MESSAGE = "Invalid code."

const formatTime = (totalSeconds: number) => {
   const minutes = Math.floor(totalSeconds / 60)
   const seconds = totalSeconds % 60
   return `${minutes}:${seconds.toString().padStart(2, "0")}`
}

const MFARequired: FC = () => {
   const dispatch = useDispatch<AppDispatch>()
   const navigate = useNavigate()
   const { challengeId, loadingLoginVerify, errorLoginVerify } = useSelector(
      ({ authReducer }: RootState) => authReducer
   )
   const [code, setCode] = useState("")
   const [secondsLeft, setSecondsLeft] = useState(INITIAL_SECONDS_LEFT)
   const [isExpired, setIsExpired] = useState(false)
   const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

   useEffect(() => {
      if (challengeId === null) {
         navigate(PageRoutes.LOGIN)
      }
   }, [challengeId, navigate])

   useEffect(() => {
      if (errorLoginVerify && errorLoginVerify !== INVALID_CODE_MESSAGE) {
         dispatch(resetAuthState())
         navigate(PageRoutes.LOGIN)
      }
   }, [errorLoginVerify, dispatch, navigate])

   useEffect(() => {
      intervalRef.current = setInterval(() => {
         setSecondsLeft((prev) => {
            if (prev <= 1) {
               if (intervalRef.current) {
                  clearInterval(intervalRef.current)
               }
               setIsExpired(true)
               return 0
            }
            return prev - 1
         })
      }, 1000)

      return () => {
         if (intervalRef.current) {
            clearInterval(intervalRef.current)
         }
      }
   }, [])

   const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH)
      setCode(digitsOnly)
   }

   const handleConfirm = useCallback(async () => {
      if (challengeId === null) {
         return
      }
      dispatch(loginVerify({ challengeId, code }))
   }, [challengeId, code, dispatch])

   const handleBackToLogin = useCallback(() => {
      dispatch(resetAuthState())
   }, [dispatch])

   const isFormValid =
      code.length === CODE_LENGTH && !isExpired && !loadingLoginVerify

   const errorMessage: ReactNode = isExpired ? (
      <>
         Verification code expired, please go back to <br /> login screen and
         try it again
      </>
   ) : errorLoginVerify === INVALID_CODE_MESSAGE ? (
      <>
         Unable to authenticate with the submitted <br /> code, please try again
      </>
   ) : null

   return (
      <AuthLayout
         iconName="shield"
         title="MFA Required"
         instructions={
            <>
               <div className="mfa-required__instructions-description">
                  This account requires multi-factor <br /> authentication.
                  Please check your email for <br /> a 6-digit verification code
                  and enter it to <br /> continue.
               </div>
               <LoginSupport />
            </>
         }
      >
         <>
            <div className="mfa-required__form-text">
               Please enter below the 6-digit code. <br /> The code will expire
               in {formatTime(secondsLeft)}
            </div>
            <TextInput
               name="code"
               variant={Variant.LARGE}
               disabled={isExpired || loadingLoginVerify}
               placeholder="6-digit code"
               value={code}
               onChange={handleChange}
            />
            <Button
               disabled={!isFormValid}
               onClick={handleConfirm}
            >
               Submit
            </Button>
            <Link
               to={PageRoutes.LOGIN}
               className="mfa-required__link"
               onClick={handleBackToLogin}
            >
               Back to login screen
            </Link>
            <div className="mfa-required__error">{errorMessage}</div>
         </>
      </AuthLayout>
   )
}

export default MFARequired
